import { useMediaQuery } from '@mui/material'
import { BigCalendar } from 'components/BigCalendar/BigCalendar'
import { Dialog } from 'components/Dialog/Dialog'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { addDays, format, formatISO } from 'date-fns'
import {
  useCreateNewDeliverySlot,
  useEditDeliverySlot,
  useGetDeliverySlot,
} from 'hooks/useServiceAreas'
import theme from 'lib/theme'
import { useAlert } from 'providers/AlertProvider'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EditSlotModal,
  ValueProps as EditSlotValueProps,
} from './EditSlotModal'
import { NewSlotModal, ValueProps } from './NewSlotModal'
import { checkValidityStart, eventStyleGetter } from './utils'

export const DeliverySlotCalendar = () => {
  const {
    placeId,
    dateData,
    setDateData,
    deliverySlots,
    isLoadingSlots,
    selectedDeliveryArea,
    allDeliveryAreas,
  } = useServiceAreas()
  const { setAlert } = useAlert()
  const [slotId, setSlotId] = useState<string | undefined>(undefined)
  const [editSlotModal, setEditSlotModal] = useState({ right: false })
  const [warning, setWarning] = useState(false)
  const { isFetching, error, deliverySlot } = useGetDeliverySlot(
    placeId,
    selectedDeliveryArea.id,
    slotId
  )
  const { mutateAsync: mutateEditSlot, isLoading: isLoadingEditRequest } =
    useEditDeliverySlot(selectedDeliveryArea.id.toString(), dateData)
  const { t } = useTranslation()
  const { mutateAsync, isLoading } = useCreateNewDeliverySlot(
    selectedDeliveryArea.id,
    dateData
  )
  const [modal, setModal] = useState({
    right: false,
  })

  const [newSlotStart, setNewSlotStart] = useState<Date | null>(null)
  const [newSlotEnd, setNewSlotEnd] = useState<Date | null>(null)
  const [tempTime, setTempTime] = useState<{
    start: Date | null
    end: Date | null
  }>({ start: null, end: null })

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleCloseModal = () => {
    setModal({ right: false })
    setEditSlotModal({ right: false })
  }

  const createNewDeliverySlot = async (values: ValueProps) => {
    const deliverySlotData = {
      delivery_slots: [
        {
          delivery_area_group_id: selectedDeliveryArea.id,
          store_id: allDeliveryAreas[0].storeId,
          start_time: format(values.startTime as Date, 'HH:mm'),
          end_time: format(values.endTime as Date, 'HH:mm'),
          date: values.recurrent
            ? Number(format(values.date as Date, 'i')) - 1
            : formatISO(values.date as Date, { representation: 'date' }),
          order_quota: Number(values.orderQuota) || 0,
          grace_hours: Number(values.graceHours),
          type: values.recurrent ? 'Recurrent' : 'Absolute',
          active: values.active,
          sequence_num: 0,
          delivery_price: Number(values.deliveryFee) || 0,
          collection_price: 0,
          delivery_type: 0,
          ...(values.attributes && {
            attributes: ['ASAP'],
          }),
          ...(values.validityStart && {
            validity_start: formatISO(values.validityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityStart && {
            visibility_start: formatISO(values.visibilityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityEnd && {
            visibility_end: formatISO(values.visibilityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.validityEnd && {
            validity_end: formatISO(values.validityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.feeProducts && {
            master_product_id: values.feeProducts,
          }),
        },
      ],
    }
    await mutateAsync(deliverySlotData, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        handleCloseModal()
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  const editDeliverySlot = async (values: EditSlotValueProps) => {
    const otherAttributes = deliverySlot?.attributes?.filter(
      (val: string) => val !== 'ASAP'
    )
    const deliverySlotData = {
      delivery_slots: [
        {
          id: Number(slotId),
          delivery_area_group_id: selectedDeliveryArea.id,
          store_id: allDeliveryAreas[0].storeId,
          start_time: format(values.startTime, 'HH:mm'),
          end_time: format(values.endTime, 'HH:mm'),
          date: values.recurrent
            ? (Number(format(values.date, 'i')) - 1).toString()
            : formatISO(values.date, { representation: 'date' }),
          order_quota: Number(values.orderQuota) || 0,
          grace_hours: Number(values.graceHours),
          type: values.recurrent ? 'Recurrent' : 'Absolute',
          active: values.active,
          sequence_num: 0,
          delivery_price: Number(values.deliveryFee) || 0,
          collection_price: 0,
          delivery_type: 0,
          attributes: values.attributes
            ? [...(otherAttributes || []), 'ASAP']
            : otherAttributes || null,
          ...(values.validityStart && {
            validity_start: formatISO(values.validityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityStart && {
            visibility_start: formatISO(values.visibilityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityEnd && {
            visibility_end: formatISO(values.visibilityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.validityEnd && {
            validity_end: formatISO(values.validityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.feeProducts && {
            master_product_id: values.feeProducts,
          }),
        },
      ],
    }
    await mutateEditSlot(deliverySlotData, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        handleCloseModal()
        setSlotId(undefined)
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  useEffect(() => {
    setModal({ right: true })
  }, [newSlotStart, newSlotEnd])

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    if (start < new Date()) {
      setTempTime({ start, end })
      setWarning(true)
    } else {
      setNewSlotStart(start)
      setNewSlotEnd(end)
    }
  }

  const handleSelectEvent = (e: {
    id: number
    end: Date
    start: Date
    title: string
  }) => {
    setSlotId(e.id.toString())
    setEditSlotModal({ right: true })
  }

  const deliveryEvents =
    deliverySlots &&
    deliverySlots
      .filter((slot) =>
        checkValidityStart(slot.date, slot.validityStart, dateData.startOfWeek)
      )
      .map((slot) => {
        const {
          deliveryPrice,
          id,
          orderQuota,
          date,
          startTime,
          endTime,
          type,
        } = slot
        const feeProductName =
          slot.product && slot.product.extraProduct === 'FEE'
            ? slot.product.name
            : null
        const formattedDate =
          date.length > 1
            ? date
            : format(
                addDays(new Date(dateData.startOfWeek), Number(date)),
                'yyyy-MM-dd'
              )
        const immediate = slot.attributes?.includes('ASAP')
        return {
          id,
          resource: type,
          immediate,
          title: feeProductName
            ? `${feeProductName} \n${orderQuota}`
            : `${parseFloat(deliveryPrice).toFixed(2)}\n${orderQuota}`,
          start: new Date(`${formattedDate}T${startTime}`),
          end: new Date(
            `${formattedDate}T${endTime === '00:00' ? '23:59' : endTime}`
          ),
        }
      })
  if (isLoadingSlots) {
    return <LoadingIndicator />
  }
  if (error) return <h1>Error</h1>
  return (
    <>
      <BigCalendar
        handleSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        events={deliveryEvents}
        dateData={dateData}
        setDateData={setDateData}
        eventStyleGetter={eventStyleGetter}
      />
      <NewSlotModal
        isLoading={isLoading}
        startTime={newSlotStart}
        endTime={newSlotEnd}
        modal={modal}
        handleCloseModal={handleCloseModal}
        handleSubmit={createNewDeliverySlot}
        deliverySlot
      />
      <Dialog
        btnColor={theme.palette.primary.main}
        dialogOpen={warning}
        handleConfirm={() => {
          setNewSlotStart(tempTime.start)
          setNewSlotEnd(tempTime.end)
        }}
        title={t('confirmSlotStart')}
        text={t('confirmSlotText')}
        handleClose={() => setWarning(false)}
        cancelText={t('cancel')}
        confirmText={t('confirm')}
      />
      {deliverySlot && (
        <EditSlotModal
          isFetching={isFetching}
          slotDetails={deliverySlot}
          isLoading={isLoadingEditRequest}
          modal={editSlotModal}
          handleCloseModal={handleCloseModal}
          handleSubmit={editDeliverySlot}
          handleDuplicate={createNewDeliverySlot}
          isLoadingDuplicate={isLoading}
          deliverySlot
        />
      )}
    </>
  )
}
